import React from 'react';
import Modal from '@material-ui/core/Modal';
import ButtonBase from '@material-ui/core/ButtonBase';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';

import { googleLoader } from '../../utils/googleLoader';

import * as S from './styles';
import { useAuth } from '../../hooks/auth';
import Button from '../../newComponents/Button';
import RestrictedAreasService from '../../services/RestrictedAreasService';
import { useToast } from '../../hooks/toast';

const RestrictedAreas: React.FC = () => {
  const { addToast } = useToast();
  const { user } = useAuth();
  let map: google.maps.Map;
  const polygon = React.useRef<any>(null);
  const [loading, setLoading] = React.useState(false);
  const [modal, setModal] = React.useState<number>();
  const [latLng, setLatLng] = React.useState<{
    lat: number;
    lng: number;
  }>({
    lat: user.empresa.lat,
    lng: user.empresa.lon,
  });

  const [areas, setAreas] = React.useState<
    {
      id: number;
      coordinates: {
        points: { lat: number; lon: number; index: number }[];
      };
    }[]
  >([]);
  const [points, setPoints] = React.useState<{ lat: number; lon: number }[]>(
    [],
  );

  const handleCreateRestrictedAreas = React.useCallback(async () => {
    setLoading(true);

    try {
      const restrictedAreas = await RestrictedAreasService.createRestrictedArea(
        user.empresa_id,
        {
          type: 1,
          coordinates: {
            points: (polygon.current as google.maps.Polygon)
              .getPath()
              .getArray()
              .map((poly, index) => ({
                index,
                lat: poly.toJSON().lat,
                lon: poly.toJSON().lng,
              })),
          },
        },
      );

      setAreas((prevState) => [
        ...prevState,
        {
          id: restrictedAreas.id,
          coordinates: restrictedAreas.coordinates,
        },
      ]);
      setPoints([]);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Ocorreu um erro',
        description: (err as any).response.data.message || 'Tente novamente',
      });
    } finally {
      setLoading(false);
    }
  }, [user, addToast]);

  const handleDeleteRestrictedAreas = React.useCallback(async () => {
    try {
      if (modal) {
        await RestrictedAreasService.deleteRestrictedArea(
          user.empresa_id,
          modal,
        );

        setAreas((prevState) => prevState.filter((area) => area.id !== modal));
        setModal(undefined);
      }
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Ocorreu um erro',
        description: (err as any).response.data.message || 'Tente novamente',
      });
    }
  }, [user, modal, addToast]);

  const handleFetchRestrictedAreas = React.useCallback(async () => {
    try {
      const restrictedAreas = await RestrictedAreasService.listRestrictedAreas(
        user.empresa_id,
      );

      setAreas(
        restrictedAreas.map((restrictedArea) => ({
          id: restrictedArea.id,
          coordinates: {
            points: restrictedArea.coordinates.points.sort(
              (a, b) => a.index - b.index,
            ),
          },
        })),
      );
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Ocorreu um erro',
        description: 'Tente novamente',
      });
    }
  }, [user, addToast]);

  React.useEffect(() => {
    handleFetchRestrictedAreas();
  }, [handleFetchRestrictedAreas]);

  React.useEffect(() => {
    googleLoader.load().then(() => {
      const myLatlng = new google.maps.LatLng(latLng.lat, latLng.lng);

      map = new google.maps.Map(
        document.getElementById('restricted-areas-map') as HTMLElement,
        {
          center: myLatlng,
          zoom: 14,
          fullscreenControl: false,
          mapTypeControl: false,
        },
      );

      const marker = new google.maps.Marker({
        position: new google.maps.LatLng(user.empresa.lat, user.empresa.lon),
        map,
        title: 'Sua empresa está aqui',
      });

      polygon.current = new google.maps.Polygon({
        geodesic: true,
        editable: true,
        strokeColor: '#ff8000',
        strokeOpacity: 1.0,
        strokeWeight: 3,
        fillColor: '#ff8000',
        map,
      });

      map.addListener('drag', console.log);

      map.addListener('click', (e) => {
        const { lat, lng } = e.latLng.toJSON();

        setPoints((prevState) => [...prevState, { lat, lon: lng }]);
      });

      areas.forEach((area) => {
        const poly = new google.maps.Polygon({
          paths: area.coordinates.points.map((point) => ({
            lat: point.lat,
            lng: point.lon,
          })),
          geodesic: true,
          strokeColor: '#d00f0f',
          strokeOpacity: 1.0,
          strokeWeight: 3,
          fillColor: '#d00f0f',
          map,
        });
      });
    });
  }, [user, areas, latLng]);

  const handleRemoveCurrentPoints = React.useCallback(() => {
    if (polygon.current) {
      polygon.current.setPath([]);
      setPoints([]);
    }
  }, [polygon]);

  React.useEffect(() => {
    if (polygon.current) {
      polygon.current.setPath(
        points.map((point) => ({ lat: point.lat, lng: point.lon })),
      );
    }
  }, [polygon, points]);

  const goToLocation = (lat: number, lng: number): void => {
    setLatLng({ lat, lng });
  };

  return (
    <S.Container>
      <S.FloatContainer>
        <S.Content>
          <header>
            <h2>Áreas restritas</h2>
          </header>
          <main>
            {areas.length > 0 ? (
              areas.map((area, index) => (
                <div key={area.id}>
                  <p>
                    Área <strong>#{String(index + 1).padStart(2, '0')}</strong>
                  </p>
                  <div>
                    <button
                      type="button"
                      onClick={() =>
                        goToLocation(
                          area.coordinates.points[0].lat,
                          area.coordinates.points[0].lon,
                        )
                      }
                    >
                      <span className="material-icons-outlined">
                        my_location
                      </span>
                    </button>
                    <button type="button" onClick={() => setModal(area.id)}>
                      <span className="material-icons-outlined">delete</span>
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p style={{ textAlign: 'center', color: '#7C8DB5' }}>
                Nenhuma área cadastrada
              </p>
            )}
            <Button
              full
              type="button"
              onClick={handleCreateRestrictedAreas}
              disabled={points.length < 3 || loading}
            >
              {loading ? 'Carregando' : 'Adicionar área'}
            </Button>
          </main>
        </S.Content>
        <S.Content style={{ padding: points.length > 0 ? 16 : 0 }}>
          {points.length > 0 ? (
            <S.Button type="button" onClick={handleRemoveCurrentPoints}>
              Remover pontos
            </S.Button>
          ) : (
            <p
              style={{
                padding: '14px 10px',
                fontSize: 14,
                fontWeight: 500,
                textAlign: 'center',
              }}
            >
              Clique no mapa para adicionar um ponto
            </p>
          )}
        </S.Content>
      </S.FloatContainer>
      <S.Map id="restricted-areas-map" />
      <Modal
        open={!!modal}
        onClose={() => setModal(undefined)}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        id="modal"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 20,
        }}
      >
        <S.ModalContainer>
          <S.ModalHeader>
            <IconButton
              aria-label="close"
              onClick={() => setModal(undefined)}
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: 'rgba(215, 222, 233, 0.25)',
              }}
            >
              <CloseIcon
                style={{
                  width: '18px',
                  height: '18px',
                  color: '#4F4F4F',
                }}
              />
            </IconButton>
          </S.ModalHeader>
          <S.ModalBody>
            <span
              style={{ color: '#F64444', fontSize: '50px' }}
              className="material-icons"
            >
              new_releases
            </span>
            <Typography
              style={{
                marginTop: '24px',
                color: '#101D40',
                fontFamily: 'Sora, sans-serif',
                fontSize: 22,
                fontWeight: 800,
                textAlign: 'center',
              }}
            >
              Deseja excluir esta área?
            </Typography>
            <Typography
              style={{
                textAlign: 'center',
                marginTop: '13px',
                fontFamily: 'Sora, sans-serif',
                fontSize: 16,
              }}
            >
              Ao confirmar, esta ação não pode mais ser desfeita. Tem certeza
              que deseja continuar?
            </Typography>
            <ButtonBase
              onClick={handleDeleteRestrictedAreas}
              style={{
                marginTop: '24px',
                width: '100%',
                height: '57px',
                borderRadius: '10px',
                backgroundColor: '#329BFC',
              }}
            >
              <Typography
                style={{
                  fontWeight: 700,
                  color: '#FFF',
                  fontFamily: 'Sora, sans-serif',
                }}
              >
                EXCLUIR
              </Typography>
            </ButtonBase>
          </S.ModalBody>
        </S.ModalContainer>
      </Modal>
    </S.Container>
  );
};

export default RestrictedAreas;
